import { useMemo, useState } from "react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { shotsApi } from "@/lib/api";
import {
  GripVertical,
  Image as ImageIcon,
  Trash2,
  Pencil,
  Copy,
  ChevronUp,
  ChevronDown,
  ArrowUpToLine,
  ArrowDownToLine,
  ArrowUpDown,
  MoreVertical,
  MoveVertical,
  Calendar as CalendarIcon,
  CheckCircle2,
  Circle,
  Loader2,
  X,
} from "lucide-react";
import { toast } from "sonner";
import StatusBadge from "@/components/StatusBadge";
import ShotEditDialog from "@/components/ShotEditDialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

const COLS = [
  { key: "scene", label: "Scene", width: "w-20" },
  { key: "shot_number", label: "Shot", width: "w-16" },
  { key: "description", label: "Description", width: "min-w-[220px]" },
  { key: "shot_size", label: "Size", width: "w-28" },
  { key: "camera_angle", label: "Angle", width: "w-28" },
  { key: "lens", label: "Lens", width: "w-20" },
  { key: "location", label: "Location", width: "w-32" },
  { key: "talent", label: "Talent", width: "w-32" },
  { key: "duration", label: "Dur.", width: "w-16" },
  { key: "scheduled_date", label: "Date", width: "w-28" },
  { key: "status", label: "Status", width: "w-32" },
];

// Columns where numeric sort makes more sense than lexicographic.
const NUMERIC_KEYS = new Set(["scene", "shot_number", "duration"]);

export function compareShots(a, b, key, dir) {
  const av = a[key];
  const bv = b[key];
  // Empty values always sink to the bottom regardless of direction —
  // otherwise ascending sort surfaces every unfilled row first.
  const aEmpty = av === null || av === undefined || av === "";
  const bEmpty = bv === null || bv === undefined || bv === "";
  if (aEmpty && bEmpty) return 0;
  if (aEmpty) return 1;
  if (bEmpty) return -1;
  let cmp;
  if (NUMERIC_KEYS.has(key)) {
    const an = parseFloat(String(av).replace(/[^0-9.-]/g, ""));
    const bn = parseFloat(String(bv).replace(/[^0-9.-]/g, ""));
    if (!Number.isNaN(an) && !Number.isNaN(bn)) {
      cmp = an - bn;
    } else {
      cmp = String(av).localeCompare(String(bv), undefined, { numeric: true });
    }
  } else {
    cmp = String(av).localeCompare(String(bv), undefined, { numeric: true, sensitivity: "base" });
  }
  return dir === "desc" ? -cmp : cmp;
}

export default function ShotTable({ projectId, shots, setShots, onReload, readOnly = false, sort: sortProp, onSortChange }) {
  const [editingShot, setEditingShot] = useState(null);
  const [moveTarget, setMoveTarget] = useState(null); // shot being moved via "Move to row #"
  const [selected, setSelected] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [scheduleDate, setScheduleDate] = useState("");
  // Column sort — null = drag order (default). {key,dir} where dir is "asc"|"desc".
  // Controllable: when `sortProp` and `onSortChange` are provided by the
  // parent (ProjectShotsPage lifts state to share sort across all 3 view
  // tabs), we defer to them. Otherwise fall back to purely-local state
  // so ShotTable remains usable in isolation.
  const [localSort, setLocalSort] = useState(null);
  const sort = sortProp !== undefined ? sortProp : localSort;
  const setSort = (updater) => {
    const next = typeof updater === "function" ? updater(sort) : updater;
    if (onSortChange) onSortChange(next);
    else setLocalSort(next);
  };

  const sortedShots = useMemo(() => {
    if (!sort) return shots;
    const copy = shots.slice();
    copy.sort((a, b) => compareShots(a, b, sort.key, sort.dir));
    return copy;
  }, [shots, sort]);

  const cycleSort = (key) => {
    setSort((prev) => {
      if (!prev || prev.key !== key) return { key, dir: "asc" };
      if (prev.dir === "asc") return { key, dir: "desc" };
      return null;
    });
  };
  const isSorted = !!sort;

  const inSelectionMode = selected.size > 0;
  const allSelected = shots.length > 0 && selected.size === shots.length;

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const selectAll = () => setSelected(new Set(shots.map((s) => s.id)));
  const clearSelection = () => setSelected(new Set());

  const handleBulkStatus = async (status) => {
    setBulkBusy(true);
    try {
      const ids = Array.from(selected);
      await shotsApi.bulkUpdate(projectId, ids, { status });
      setShots((prev) => prev.map((s) => (selected.has(s.id) ? { ...s, status } : s)));
      toast.success(`Updated ${ids.length} ${ids.length === 1 ? "shot" : "shots"}`);
      clearSelection();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk update failed");
    } finally {
      setBulkBusy(false);
    }
  };

  const handleBulkSchedule = async () => {
    if (!scheduleDate) return;
    setBulkBusy(true);
    try {
      const ids = Array.from(selected);
      await shotsApi.bulkUpdate(projectId, ids, { scheduled_date: scheduleDate });
      setShots((prev) =>
        prev.map((s) => (selected.has(s.id) ? { ...s, scheduled_date: scheduleDate } : s))
      );
      toast.success(`Scheduled ${ids.length} ${ids.length === 1 ? "shot" : "shots"}`);
      setScheduleOpen(false);
      setScheduleDate("");
      clearSelection();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Schedule failed");
    } finally {
      setBulkBusy(false);
    }
  };

  const handleBulkDelete = async () => {
    if (!window.confirm(`Delete ${selected.size} selected shots? This can't be undone.`)) return;
    setBulkBusy(true);
    try {
      const ids = Array.from(selected);
      const res = await shotsApi.bulkDelete(projectId, ids);
      setShots((prev) => prev.filter((s) => !selected.has(s.id)));
      toast.success(`Deleted ${res.deleted}`);
      clearSelection();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk delete failed");
    } finally {
      setBulkBusy(false);
    }
  };

  const persistOrder = async (newList) => {
    setShots(newList);
    try {
      await shotsApi.reorder(
        projectId,
        newList.map((s) => s.id)
      );
    } catch (e) {
      toast.error("Failed to save order");
    }
  };

  const moveTo = (index, newIndex) => {
    if (newIndex < 0 || newIndex >= shots.length || newIndex === index) return;
    const next = Array.from(shots);
    const [m] = next.splice(index, 1);
    next.splice(newIndex, 0, m);
    persistOrder(next);
  };

  const onDragEnd = async (result) => {
    if (!result.destination) return;
    const from = result.source.index;
    const to = result.destination.index;
    if (from === to) return;
    const reordered = Array.from(shots);
    const [moved] = reordered.splice(from, 1);
    reordered.splice(to, 0, moved);
    persistOrder(reordered);
  };

  const handleDelete = async (id) => {
    try {
      await shotsApi.remove(id);
      setShots((s) => s.filter((x) => x.id !== id));
      toast.success("Shot deleted");
    } catch (e) {
      toast.error("Failed to delete");
    }
  };

  const handleDuplicate = async (shot, idx) => {
    try {
      const copy = await shotsApi.duplicate(shot.id);
      const next = Array.from(shots);
      next.splice(idx + 1, 0, copy);
      setShots(next);
      toast.success("Shot duplicated");
    } catch (e) {
      toast.error("Failed to duplicate");
    }
  };

  const handleStatusChange = async (id, status) => {
    try {
      const updated = await shotsApi.update(id, { status });
      setShots((s) => s.map((x) => (x.id === id ? updated : x)));
    } catch (e) {
      toast.error("Failed to update status");
    }
  };

  if (shots.length === 0) {
    return (
      <div className="border border-dashed border-zinc-800 rounded-md p-12 text-center">
        <div className="text-zinc-500 text-sm">
          No shots yet. Add one manually or use the AI generator above.
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Bulk action bar — appears above the table when shots are selected */}
      {inSelectionMode && !readOnly && (
        <div
          data-testid="shots-bulk-bar"
          className="mb-3 flex items-center justify-between gap-3 flex-wrap p-2.5 rounded-sm bg-amber-500/10 border border-amber-500/30"
        >
          <div className="flex items-center gap-3">
            <span
              data-testid="shots-bulk-count"
              className="text-sm font-semibold text-amber-400"
            >
              {selected.size} selected
            </span>
            {!allSelected && (
              <button
                onClick={selectAll}
                data-testid="shots-select-all-action"
                className="text-xs text-zinc-400 hover:text-zinc-100 underline"
              >
                Select all ({shots.length})
              </button>
            )}
          </div>
          <div className="flex items-center gap-1.5 flex-wrap">
            <button
              data-testid="bulk-mark-not-shot"
              onClick={() => handleBulkStatus("not_shot")}
              disabled={bulkBusy}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs bg-zinc-900 border border-zinc-800 text-zinc-300 hover:text-zinc-100 hover:border-zinc-700 rounded-sm disabled:opacity-60"
            >
              <Circle className="w-3 h-3" /> Not Shot
            </button>
            <button
              data-testid="bulk-mark-in-progress"
              onClick={() => handleBulkStatus("in_progress")}
              disabled={bulkBusy}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs bg-zinc-900 border border-amber-500/40 text-amber-400 hover:bg-amber-500/10 rounded-sm disabled:opacity-60"
            >
              <Loader2 className="w-3 h-3" /> In Progress
            </button>
            <button
              data-testid="bulk-mark-completed"
              onClick={() => handleBulkStatus("completed")}
              disabled={bulkBusy}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs bg-zinc-900 border border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10 rounded-sm disabled:opacity-60"
            >
              <CheckCircle2 className="w-3 h-3" /> Completed
            </button>
            <div className="relative">
              <button
                data-testid="bulk-schedule-btn"
                onClick={() => setScheduleOpen((v) => !v)}
                disabled={bulkBusy}
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs bg-zinc-900 border border-zinc-800 text-zinc-300 hover:border-zinc-700 rounded-sm disabled:opacity-60"
              >
                <CalendarIcon className="w-3 h-3" /> Schedule…
              </button>
              {scheduleOpen && (
                <div className="absolute right-0 top-full mt-1 z-50 p-2 bg-zinc-950 border border-zinc-800 rounded-sm shadow-2xl flex items-center gap-2">
                  <input
                    data-testid="bulk-schedule-date-input"
                    type="date"
                    value={scheduleDate}
                    onChange={(e) => setScheduleDate(e.target.value)}
                    className="bg-zinc-950 border border-zinc-800 rounded-sm px-2 py-1 text-xs text-zinc-100"
                  />
                  <button
                    onClick={handleBulkSchedule}
                    disabled={!scheduleDate || bulkBusy}
                    data-testid="bulk-schedule-apply"
                    className="px-2 py-1 text-xs bg-amber-500 text-black font-semibold rounded-sm disabled:opacity-50"
                  >
                    Apply
                  </button>
                </div>
              )}
            </div>
            <button
              data-testid="bulk-delete-shots-btn"
              onClick={handleBulkDelete}
              disabled={bulkBusy}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs bg-transparent border border-rose-500/40 text-rose-400 hover:bg-rose-500/10 rounded-sm disabled:opacity-60"
            >
              <Trash2 className="w-3 h-3" /> Delete
            </button>
            <button
              data-testid="shots-bulk-clear"
              onClick={clearSelection}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900 rounded-sm"
            >
              <X className="w-3 h-3" /> Clear
            </button>
          </div>
        </div>
      )}

      <div className="border border-zinc-800 rounded-md overflow-hidden bg-zinc-900">
        <div className="overflow-x-auto">
          {isSorted && (
            <div
              data-testid="shots-sort-chip"
              className="flex items-center justify-between gap-3 px-3 py-2 border-b border-zinc-800 bg-amber-500/5 text-xs"
            >
              <div className="text-amber-400">
                Sorted by{" "}
                <span className="font-semibold">
                  {(COLS.find((c) => c.key === sort.key) || {}).label}
                </span>{" "}
                <span className="text-amber-500/70">
                  ({sort.dir === "asc" ? "A → Z" : "Z → A"})
                </span>
                <span className="text-zinc-500 ml-2">
                  · Drag-to-reorder is paused while sorted.
                </span>
              </div>
              <button
                type="button"
                data-testid="shots-clear-sort"
                onClick={() => setSort(null)}
                className="inline-flex items-center gap-1 text-zinc-400 hover:text-amber-400"
              >
                <X className="w-3 h-3" /> Clear sort
              </button>
            </div>
          )}
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-zinc-950/80 border-b border-zinc-800">
                {!readOnly && (
                  <th className="w-8 px-2 py-3">
                    {shots.length > 0 && (
                      <input
                        type="checkbox"
                        data-testid="shots-select-all-checkbox"
                        checked={allSelected}
                        onChange={(e) => (e.target.checked ? selectAll() : clearSelection())}
                        className="w-3.5 h-3.5 accent-amber-500 cursor-pointer"
                        title="Select all"
                      />
                    )}
                  </th>
                )}
                <th className="w-28 px-2 py-3 text-left text-overline text-zinc-500">Order</th>
                <th className="w-10 px-2 py-3"></th>
                {COLS.map((c) => {
                  const active = sort && sort.key === c.key;
                  const Icon = active
                    ? sort.dir === "asc"
                      ? ChevronUp
                      : ChevronDown
                    : ArrowUpDown;
                  return (
                    <th
                      key={c.key}
                      className={`${c.width} text-left px-3 py-3 text-overline whitespace-nowrap`}
                    >
                      <button
                        type="button"
                        data-testid={`shots-sort-${c.key}`}
                        onClick={() => cycleSort(c.key)}
                        title={
                          active
                            ? `Sort by ${c.label} · ${
                                sort.dir === "asc" ? "descending" : "clear sort"
                              } next`
                            : `Sort by ${c.label}`
                        }
                        className={`inline-flex items-center gap-1 group hover:text-amber-400 transition-colors ${
                          active ? "text-amber-400" : "text-zinc-500"
                        }`}
                      >
                        <span>{c.label}</span>
                        <Icon
                          className={`w-3 h-3 ${
                            active
                              ? "text-amber-400"
                              : "text-zinc-700 group-hover:text-amber-400/70"
                          }`}
                        />
                      </button>
                    </th>
                  );
                })}
                <th className="w-32 px-2 py-3"></th>
              </tr>
            </thead>
            <DragDropContext onDragEnd={onDragEnd}>
              <Droppable droppableId="shots" isDropDisabled={isSorted}>
                {(provided) => (
                  <tbody ref={provided.innerRef} {...provided.droppableProps}>
                    {sortedShots.map((shot, idx) => (
                      <Draggable
                        key={shot.id}
                        draggableId={shot.id}
                        index={idx}
                        isDragDisabled={isSorted}
                      >
                        {(prov, snapshot) => (
                          <tr
                            ref={prov.innerRef}
                            {...prov.draggableProps}
                            data-testid={`shot-row-${shot.id}`}
                            className={`border-b border-zinc-800/60 hover:bg-zinc-800/30 transition-colors ${
                              selected.has(shot.id) ? "bg-amber-500/[0.04]" : ""
                            } ${
                              snapshot.isDragging ? "drag-row-dragging" : ""
                            }`}
                            style={prov.draggableProps.style}
                          >
                            {!readOnly && (
                              <td className="px-2 py-3 align-middle">
                                <input
                                  type="checkbox"
                                  data-testid={`shot-select-${shot.id}`}
                                  checked={selected.has(shot.id)}
                                  onChange={() => toggleSelect(shot.id)}
                                  className="w-3.5 h-3.5 accent-amber-500 cursor-pointer"
                                />
                              </td>
                            )}
                            <td className="px-2 py-3">
                              <div className="flex items-center gap-0.5">
                                <button
                                  {...prov.dragHandleProps}
                                  data-testid={`shot-drag-${shot.id}`}
                                  title={isSorted ? "Clear sort to drag-reorder" : "Drag to reorder"}
                                  disabled={isSorted}
                                  className={`p-1 ${
                                    isSorted
                                      ? "text-zinc-800 cursor-not-allowed"
                                      : "text-zinc-600 hover:text-zinc-300 cursor-grab active:cursor-grabbing"
                                  }`}
                                >
                                  <GripVertical className="w-4 h-4" />
                                </button>
                                <span className="text-xs font-mono text-zinc-500 w-5 text-center">
                                  {idx + 1}
                                </span>
                                <div className="flex flex-col">
                                  <button
                                    data-testid={`shot-up-${shot.id}`}
                                    onClick={() => moveTo(idx, idx - 1)}
                                    disabled={idx === 0 || isSorted}
                                    title={isSorted ? "Clear sort to reorder" : "Move up"}
                                    className="text-zinc-600 hover:text-amber-500 disabled:opacity-30 disabled:hover:text-zinc-600 disabled:cursor-not-allowed p-0.5"
                                  >
                                    <ChevronUp className="w-3.5 h-3.5" strokeWidth={2.5} />
                                  </button>
                                  <button
                                    data-testid={`shot-down-${shot.id}`}
                                    onClick={() => moveTo(idx, idx + 1)}
                                    disabled={idx === shots.length - 1 || isSorted}
                                    title={isSorted ? "Clear sort to reorder" : "Move down"}
                                    className="text-zinc-600 hover:text-amber-500 disabled:opacity-30 disabled:hover:text-zinc-600 disabled:cursor-not-allowed p-0.5"
                                  >
                                    <ChevronDown className="w-3.5 h-3.5" strokeWidth={2.5} />
                                  </button>
                                </div>
                              </div>
                            </td>
                            <td className="px-2 py-3">
                              <ShotThumb shot={shot} />
                            </td>
                            {COLS.map((c) => (
                              <td key={c.key} className={`${c.width} px-3 py-3 text-sm text-zinc-300 align-middle`}>
                                {c.key === "status" ? (
                                  <StatusBadge
                                    value={shot.status}
                                    onChange={(v) => handleStatusChange(shot.id, v)}
                                    testId={`status-${shot.id}`}
                                  />
                                ) : c.key === "description" ? (
                                  <div className="whitespace-pre-wrap break-words text-zinc-200 leading-relaxed">
                                    {shot.description || (
                                      <span className="text-zinc-600 italic">—</span>
                                    )}
                                  </div>
                                ) : (
                                  <span className={shot[c.key] ? "text-zinc-300" : "text-zinc-600"}>
                                    {shot[c.key] || "—"}
                                  </span>
                                )}
                              </td>
                            ))}
                            <td className="px-2 py-3">
                              <div className="flex items-center gap-1 opacity-60 group-hover:opacity-100">
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <button
                                      data-testid={`shot-actions-${shot.id}`}
                                      title="Reorder actions"
                                      className="p-1.5 rounded-sm text-zinc-500 hover:text-zinc-100 hover:bg-zinc-800"
                                    >
                                      <MoreVertical className="w-3.5 h-3.5" />
                                    </button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent
                                    align="end"
                                    className="bg-zinc-950 border-zinc-800 text-zinc-100"
                                  >
                                    <DropdownMenuLabel className="text-xs text-zinc-500">
                                      Reorder
                                    </DropdownMenuLabel>
                                    <DropdownMenuItem
                                      data-testid={`move-top-${shot.id}`}
                                      disabled={idx === 0 || isSorted}
                                      onClick={() => moveTo(idx, 0)}
                                      className="focus:bg-zinc-900 focus:text-zinc-100 cursor-pointer"
                                    >
                                      <ArrowUpToLine className="w-3.5 h-3.5 mr-2" /> Move to top
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                      data-testid={`move-bottom-${shot.id}`}
                                      disabled={idx === shots.length - 1 || isSorted}
                                      onClick={() => moveTo(idx, shots.length - 1)}
                                      className="focus:bg-zinc-900 focus:text-zinc-100 cursor-pointer"
                                    >
                                      <ArrowDownToLine className="w-3.5 h-3.5 mr-2" /> Move to bottom
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                      data-testid={`move-to-${shot.id}`}
                                      disabled={isSorted}
                                      onClick={() => setMoveTarget({ shot, idx })}
                                      className="focus:bg-zinc-900 focus:text-zinc-100 cursor-pointer"
                                    >
                                      <MoveVertical className="w-3.5 h-3.5 mr-2" /> Move to row…
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                                <button
                                  data-testid={`duplicate-shot-${shot.id}`}
                                  onClick={() => handleDuplicate(shot, idx)}
                                  title="Duplicate shot"
                                  className="p-1.5 rounded-sm text-zinc-500 hover:text-amber-500 hover:bg-zinc-800"
                                >
                                  <Copy className="w-3.5 h-3.5" />
                                </button>
                                <button
                                  data-testid={`edit-shot-${shot.id}`}
                                  onClick={() => setEditingShot(shot)}
                                  className="p-1.5 rounded-sm text-zinc-500 hover:text-zinc-100 hover:bg-zinc-800"
                                >
                                  <Pencil className="w-3.5 h-3.5" />
                                </button>
                                <button
                                  data-testid={`delete-shot-${shot.id}`}
                                  onClick={() => handleDelete(shot.id)}
                                  className="p-1.5 rounded-sm text-zinc-500 hover:text-rose-400 hover:bg-zinc-800"
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </tbody>
                )}
              </Droppable>
            </DragDropContext>
          </table>
        </div>
      </div>

      {editingShot && (
        <ShotEditDialog
          shot={editingShot}
          onClose={() => setEditingShot(null)}
          onSaved={(updated) => {
            setShots((s) => s.map((x) => (x.id === updated.id ? updated : x)));
            setEditingShot(null);
          }}
        />
      )}

      {moveTarget && (
        <MoveToRowDialog
          shot={moveTarget.shot}
          currentIndex={moveTarget.idx}
          total={shots.length}
          onClose={() => setMoveTarget(null)}
          onMove={(newIdx) => {
            moveTo(moveTarget.idx, newIdx);
            setMoveTarget(null);
          }}
        />
      )}
    </>
  );
}

function MoveToRowDialog({ shot, currentIndex, total, onClose, onMove }) {
  const [value, setValue] = useState(String(currentIndex + 1));
  const handleSubmit = (e) => {
    e.preventDefault();
    const target = parseInt(value, 10);
    if (Number.isNaN(target) || target < 1 || target > total) {
      toast.error(`Enter a number between 1 and ${total}`);
      return;
    }
    onMove(target - 1);
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 sm:max-w-sm">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl font-light">
            Move shot to row…
          </DialogTitle>
          <DialogDescription className="text-zinc-500">
            Currently at row {currentIndex + 1} of {total}.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <Label htmlFor="row" className="text-overline text-zinc-500">
              New row position
            </Label>
            <Input
              id="row"
              data-testid="move-to-row-input"
              type="number"
              min={1}
              max={total}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              className="bg-zinc-950 border-zinc-800 focus-visible:ring-amber-500 mt-1.5"
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="bg-transparent border-zinc-800 text-zinc-300 hover:bg-zinc-900 hover:text-zinc-100"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              data-testid="confirm-move-btn"
              className="bg-amber-500 text-black hover:bg-amber-400 rounded-sm"
            >
              Move
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ShotThumb({ shot }) {
  if (shot.image_data) {
    return (
      <img
        src={shot.image_data}
        alt="storyboard"
        className="w-10 h-10 rounded-sm object-cover border border-zinc-800"
      />
    );
  }
  return (
    <div className="w-10 h-10 rounded-sm bg-zinc-950 border border-zinc-800 flex items-center justify-center text-zinc-700">
      <ImageIcon className="w-3.5 h-3.5" />
    </div>
  );
}

